// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="DateRangePicker" set="mytaible-shadcn-studio" verified=tbd

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { cn } from '../../../lib/utils';
import { Button } from '../../atoms/button';
import { Calendar } from '../../molecules/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../molecules/popover';

// ─── Re-export ────────────────────────────────────────────────────────────────

export type { DateRange };

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DateRangePickerProps {
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  numberOfMonths?: number;
  className?: string;
}

// ─── DateRangePicker ──────────────────────────────────────────────────────────

const DateRangePicker = React.forwardRef<HTMLButtonElement, DateRangePickerProps>(
  (
    {
      dateRange,
      onDateRangeChange,
      placeholder = 'Pick a date range',
      disabled,
      numberOfMonths = 2,
      className,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);

    const label = dateRange?.from
      ? dateRange.to
        ? `${format(dateRange.from, 'LLL dd, y')} – ${format(dateRange.to, 'LLL dd, y')}`
        : format(dateRange.from, 'LLL dd, y')
      : null;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="secondary"
            appearance="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !label && 'text-muted-foreground',
              className,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
            {label ?? <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" aria-label="Date range picker" align="start">
          <Calendar
            mode="range"
            selected={dateRange}
            onSelect={onDateRangeChange}
            numberOfMonths={numberOfMonths}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  },
);
DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
